/**
 * Draft-mode type definitions. Mirrors the web app's draftSlice state shape
 * (frontend-prod/app/store/reducers/draftSlice.ts) but trimmed to what the
 * Word-plugin's overlay-based flow needs. Field names match the AI agent's
 * wire format (section_header → header) after normalisation.
 */

export type DraftPhase =
  | 'idle' //          no draft in progress
  | 'prompt' //        user is writing the brief (regular chat input, no overlay)
  | 'template_loading' // sent generate_template, waiting for SSE
  | 'template_review' //  template arrived; user reviews / reorders sections
  | 'questions_loading' // sent generate_questions, waiting
  | 'qna' //              questions arrived; user answers (non-dismissible)
  | 'draft_loading' //    sent generate_draft, waiting for DOCX
  | 'instant_loading' //  F1 — Auto/Instant mode: brief→DOCX directly, no template/QnA UI
  | 'complete'; //        DOCX applied to Word, conversation continues as edit-like

/**
 * F1 — Auto vs Guided draft experience.
 *
 *   'interactive' — full 3-phase flow: template review → QnA → draft.
 *                   Default. Same as the legacy behaviour.
 *   'instant'     — backend skips QnA; brief → DOCX directly. Sent on the
 *                   generate_template payload's top-level `draft_experience`
 *                   field (NOT inside metadata), matching frontend-prod
 *                   `app/draft/[id]/page.tsx:360`.
 *
 * SOURCE: frontend-prod/features/draft/PromptInputScene.tsx:286-359
 */
export type DraftExperience = 'interactive' | 'instant';

/**
 * F3 — Concise vs Detailed processing mode.
 *
 *   'speed'    — Concise. Faster generation, fewer LLM hops.
 *   'accuracy' — Detailed. More thorough section work.
 *
 * Sent at the TOP level of every draft message payload (template / questions
 * / generate), matching frontend-prod's `mode: 'speed' | 'accuracy'` field at
 * `app/draft/[id]/page.tsx:364, 884`. Backend reads it as `Mode string`
 * (`common_models.go:57`) and AI maps it to `state["processing_mode"]` at
 * `agent_runner.py:802`.
 *
 * SOURCE: frontend-prod/features/draft/PromptInputScene.tsx:413-453
 */
export type ProcessingMode = 'speed' | 'accuracy';

/** A subsection under a section. Mirrors the AI agent's `section_subheaders` entry. */
export interface TemplateSubheader {
  id: string;
  /** Hierarchical numbering from the agent (e.g. "1.1"). */
  number?: string;
  title: string;
  description?: string;
}

/** Top-level section. Mirrors the AI agent's `template.sections[]` entry. */
export interface TemplateSection {
  id: string;
  /** Hierarchical section number from the agent (e.g. "1"). */
  number?: string;
  /** Human-readable heading (maps from server's `section_header`). */
  header: string;
  /** 2-3 sentence description surfaced in the collapsible "Why this section" panel. */
  description?: string;
  /** Checklist of must-include items (chips in the expanded view). */
  mustInclude?: string[];
  /** Free-form guidance for the generation agent (rendered in expanded view). */
  guidance?: string;
  expectedDepth?: 'exhaustive' | 'standard' | 'brief';
  complexity?: 'standard' | 'complex';
  subheaders: TemplateSubheader[];
}

export interface QuestionAnswer {
  id: string;
  question: string;
  /** `null` until the user types; blank string is explicit intent to skip. */
  answer: string | null;
}

/**
 * Snapshot of an in-flight draft, persisted in Office.context.document.settings.
 * Lets the user close + reopen Word during QnA without losing progress.
 */
export interface DraftSessionState {
  chatId: string | null;
  featureId: string | null;
  phase: DraftPhase;
  prompt: string;
  template: TemplateSection[];
  questions: QuestionAnswer[];
  draftS3Key: string | null;
  /** F1 — user's chosen draft experience. Defaults to 'interactive'. */
  draftExperience: DraftExperience;
  /** F3 — quality / speed trade-off. Defaults to 'speed' (Concise). */
  processingMode: ProcessingMode;
  updatedAt: number;
}

export const EMPTY_DRAFT_STATE: DraftSessionState = {
  chatId: null,
  featureId: null,
  phase: 'idle',
  prompt: '',
  template: [],
  questions: [],
  draftS3Key: null,
  draftExperience: 'interactive',
  processingMode: 'speed',
  updatedAt: 0,
};
